import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';

const CooperativeMachinePerformanceReportSecretary = ({ user }) => {
  const navigate = useNavigate();

  // Filter States
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Report Data States
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    noOfMachines: 0,
    chargeableHrs: 0,
    freeHrs: 0,
    totalUtilization: 0,
    avgUtilization: 0,
    totalRevenue: 0,
    avgRevenue: 0
  });

  // Helper to convert DD-Mon-YYYY to YYYY-MM-DD
  const parseDateToISO = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';
    const monthMap = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
    const day = parts[0].padStart(2, '0');
    const month = monthMap[parts[1]] || '01';
    const year = parts[2];
    return `${year}-${month}-${day}`;
  };

  // Fetch Periods for Dropdown & Select Latest by Default
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/periods`);
        const data = await res.json();
        if (data.success && Array.isArray(data.periods) && data.periods.length > 0) {
          setPeriods(data.periods);
          const latest = data.periods[0]; // Sorted latest first
          setSelectedPeriodId(latest._id);
          setSelectedPeriod(latest);
          if (latest.StartDate && latest.EndDate) {
            setStartDate(parseDateToISO(latest.StartDate));
            setEndDate(parseDateToISO(latest.EndDate));
          }
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
      }
    };
    fetchPeriods();
  }, []);

  const [showAllMachines, setShowAllMachines] = useState(false);

  // Fetch Report Data from API (Manual trigger via Search)
  const fetchReportData = useCallback(async (perId = selectedPeriodId, sDate = startDate, eDate = endDate, isShowAll = showAllMachines) => {
    if (!sDate || !eDate) return;
    setLoading(true);
    try {
      const societyId = user?.society ? user.society._id : '';
      const queryParams = new URLSearchParams({
        societyId,
        periodId: perId,
        startDate: sDate,
        endDate: eDate,
        showAll: isShowAll ? 'true' : 'false'
      });

      const res = await fetch(`${API_BASE_URL}/api/reports/performance?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success) {
        setRows(data.rows || []);
        setTotals(data.totals || {});
      }
    } catch (err) {
      console.error('Error fetching performance report:', err);
    } finally {
      setLoading(false);
    }
  }, [user, selectedPeriodId, startDate, endDate, showAllMachines]);

  // Initial load when period and dates are initialized
  const [initialLoaded, setInitialLoaded] = useState(false);
  useEffect(() => {
    if (!initialLoaded && startDate && endDate) {
      fetchReportData(selectedPeriodId, startDate, endDate, showAllMachines);
      setInitialLoaded(true);
    }
  }, [startDate, endDate, selectedPeriodId, fetchReportData, initialLoaded, showAllMachines]);

  // Search Handler
  const handleSearch = () => {
    fetchReportData(selectedPeriodId, startDate, endDate, showAllMachines);
  };

  // Toggle Show More / Show Less Machines
  const handleToggleShowMachines = () => {
    const nextVal = !showAllMachines;
    setShowAllMachines(nextVal);
    fetchReportData(selectedPeriodId, startDate, endDate, nextVal);
  };

  // Clear Handler (resets filters to default latest period)
  const handleClear = () => {
    setShowAllMachines(false);
    if (periods.length > 0) {
      const latest = periods[0];
      setSelectedPeriodId(latest._id);
      setSelectedPeriod(latest);
      if (latest.StartDate && latest.EndDate) {
        const sISO = parseDateToISO(latest.StartDate);
        const eISO = parseDateToISO(latest.EndDate);
        setStartDate(sISO);
        setEndDate(eISO);
        fetchReportData(latest._id, sISO, eISO, false);
      }
    }
  };

  // Handle Period Dropdown Selection (Auto updates Start and End Date)
  const handlePeriodChange = (e) => {
    const periodId = e.target.value;
    setSelectedPeriodId(periodId);
    const matched = periods.find(p => p._id === periodId);
    if (matched) {
      setSelectedPeriod(matched);
      if (matched.StartDate && matched.EndDate) {
        setStartDate(parseDateToISO(matched.StartDate));
        setEndDate(parseDateToISO(matched.EndDate));
      }
    }
  };

  // Restrict Start & End date pickers strictly between period boundaries
  const periodMinISO = selectedPeriod ? parseDateToISO(selectedPeriod.StartDate) : '';
  const periodMaxISO = selectedPeriod ? parseDateToISO(selectedPeriod.EndDate) : '';

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    if (periodMinISO && val < periodMinISO) return;
    if (periodMaxISO && val > periodMaxISO) return;
    setStartDate(val);
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    if (periodMinISO && val < periodMinISO) return;
    if (periodMaxISO && val > periodMaxISO) return;
    setEndDate(val);
  };

  // Export CSV Handler
  const handleExportCSV = () => {
    if (rows.length === 0) return;
    
    const headers = ["Machines", "No. of Machines", "Authorized Machine Count", "Chargeable Hrs", "Free Hrs", "Total Utilization", "Avg. Utilization", "Total Revenue", "Avg. Revenue"];
    const csvRows = rows.map(r => [
      `"${r.name}"`,
      r.noOfMachines,
      r.authNoOfMachines || 0,
      r.chargeableHrs,
      r.freeHrs,
      r.totalUtilization,
      r.avgUtilization,
      r.totalRevenue,
      r.avgRevenue
    ].join(','));

    csvRows.push([
      "TOTALS",
      totals.noOfMachines,
      totals.authNoOfMachines || 0,
      totals.chargeableHrs,
      totals.freeHrs,
      totals.totalUtilization,
      totals.avgUtilization,
      totals.totalRevenue,
      totals.avgRevenue
    ].join(','));

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...csvRows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Cooperative_Machine_Performance_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render Filter Bar
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
        {/* Period Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Period:</label>
          <select
            value={selectedPeriodId}
            onChange={handlePeriodChange}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none',
              fontWeight: '500'
            }}
          >
            {periods.map(p => {
              const label = `${p.StartDate} to ${p.EndDate} (${p.seasonName || p.PERIOD})`;
              return <option key={p._id} value={p._id}>{label}</option>;
            })}
          </select>
        </div>

        {/* Date Range Inputs restricted to period boundaries */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Date Range:</label>
          <input
            type="date"
            value={startDate}
            min={periodMinISO}
            max={periodMaxISO}
            onChange={handleStartDateChange}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
          <input
            type="date"
            value={endDate}
            min={periodMinISO}
            max={periodMaxISO}
            onChange={handleEndDateChange}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
        </div>

        {/* Search & Clear Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSearch}
            style={{
              background: '#f59e0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Right Side Buttons above Grid */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleToggleShowMachines}
          style={{
            background: '#4318ff',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {showAllMachines ? 'Show Less (Top 13)' : 'Show More (All Machines)'}
        </button>

        {/* Export CSV Button */}
        <button
          type="button"
          onClick={handleExportCSV}
          disabled={rows.length === 0}
          style={{
            background: rows.length > 0 ? '#10b981' : '#cbd5e1',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: rows.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          📊 Export CSV
        </button>
      </div>
    </div>
  );

  return (
    <ListContainer
      title="Cooperative Machine Performance Report"
      onClose={() => navigate('/home')}
      filterBar={filterBar}
    >
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#ffffff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', verticalAlign: 'middle', width: '200px' }}>
                Machines
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '120px' }}>
                No. of Machines
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '150px' }}>
                Authorized Machine Count
              </th>
              <th colSpan="2" style={{ padding: '10px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'center', background: '#ffffff' }}>
                Utilization
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '130px' }}>
                Total Utilization
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '130px' }}>
                Avg. Utilization
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '130px' }}>
                Total Revenue
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', fontWeight: '700', textAlign: 'right', verticalAlign: 'middle', width: '130px' }}>
                Avg. Revenue
              </th>
            </tr>
            <tr style={{ background: '#2d3748', color: '#ffffff' }}>
              <th style={{ padding: '8px 14px', borderRight: '1px solid #4a5568', textAlign: 'right', fontWeight: '600', width: '120px' }}>Chargeable Hrs</th>
              <th style={{ padding: '8px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600', width: '100px' }}>Free Hrs</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ padding: '40px', textAlign: 'center' }}>
                  <div className="loading-spinner-container">
                    <div className="spinner"></div>
                    <div>Loading Performance Report...</div>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                  No performance data available.
                </td>
              </tr>
            ) : (
              <>
                {rows.map((r) => (
                  <tr key={r.code} style={{ borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '500', borderRight: '1px solid #f1f5f9' }}>{r.name}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9' }}>{r.noOfMachines}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9', fontWeight: '600' }}>{r.authNoOfMachines || 0}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9' }}>{r.chargeableHrs}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9' }}>{r.freeHrs}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9', fontWeight: '600' }}>{r.totalUtilization.toFixed(2)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9' }}>{r.avgUtilization.toFixed(2)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', borderRight: '1px solid #f1f5f9' }}>{r.totalRevenue}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>{r.avgRevenue}</td>
                  </tr>
                ))}

                {/* TOTALS Summary Row */}
                <tr style={{ background: '#f8fafc', fontWeight: '700', color: '#1e293b', borderTop: '2px solid #cbd5e1' }}>
                  <td style={{ padding: '14px 16px', borderRight: '1px solid #cbd5e1' }}>TOTALS</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.noOfMachines}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.authNoOfMachines || 0}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.chargeableHrs.toFixed(2)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.freeHrs.toFixed(2)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.totalUtilization.toFixed(2)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.avgUtilization.toFixed(2)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.totalRevenue.toFixed(2)}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>{totals.avgRevenue.toFixed(2)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </ListContainer>
  );
};

export default CooperativeMachinePerformanceReportSecretary;
